import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  FiChevronRight, FiFolder, FiUser,
  FiFile, FiFileText, FiImage, FiCode, FiArchive,
  FiEye, FiDatabase, FiClipboard, FiDollarSign, FiRefreshCw, FiPaperclip, FiZap, FiInfo,
} from "react-icons/fi";
import type { IconType } from "react-icons";
import { useAuthFetch } from "../lib/authFetch";
import { useCorpora, corpusLabel, CorpusListing } from "../lib/useCorpora";
import { JsonTreeNode } from "./JsonTree";
import { Chart as ChartJS, registerables } from "chart.js";
import { Chart } from "react-chartjs-2";

// Docling figures carry arbitrary chart.js configs (bar, line, pie, …), so
// register everything — controllers included. Registering only elements
// (the previous approach) crashes with '"bar" is not a registered
// controller' because type lookup needs the controller, not the element.
ChartJS.register(...registerables);

// react-icons v5 IconType return type is ReactNode, not JSX.Element;
// this wrapper bridges the gap without any cast.
function Ic({ icon, className }: { icon: IconType; className: string }) {
  return React.createElement(icon as React.ElementType, { className });
}

// ── Config ────────────────────────────────────────────────────────────────────

const CORPUS_API = process.env.REACT_APP_CORPUS_API_URL ?? "http://localhost:3002";

// ── Types ─────────────────────────────────────────────────────────────────────

interface UploadRecord {
  fileId: string;
  owner: string;
  filename: string;
  fileType: string;
  fileSize: number;
  s3Key: string;
  status: string;
  uploadedAt: string;
  parsedS3Key: string | null;
  /** From uploads.format_hint when present (email / edgar / transcript). */
  formatHint?: string | null;
  /** Joined server-side from processed_documents (#112). */
  documentType?: string | null;
  author?: string | null;
  /** AI-suggested canonical name — preferred for display (#116). */
  newFilename?: string | null;
}

interface UploadResult {
  filename: string;
  status: "success" | "duplicate" | "error";
  message: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function sanitizeText(raw: string): string {
  // Replace BEL (\u0007) and other non-printable control chars that docling
  // sometimes emits in place of spaces, then normalise runs of whitespace.
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0007\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, " ").replace(/ +/g, " ");
}


function formatBytes(bytes: number): string {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function FileTypeIcon({ filename, fileType, className = "w-4 h-4" }: { filename: string; fileType: string; className?: string }) {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  let icon: IconType = FiFile;
  let col = "text-kfaint";
  if (fileType?.startsWith("image/") || ["jpg","jpeg","png","gif","webp","svg"].includes(ext)) { icon = FiImage;    col = "text-pink-400"; }
  else if (ext === "pdf" || fileType === "application/pdf")                                    { icon = FiFileText; col = "text-red-500";  }
  else if (["doc","docx"].includes(ext))                                                       { icon = FiFileText; col = "text-ksky"; }
  else if (["xls","xlsx","csv"].includes(ext))                                                 { icon = FiFileText; col = "text-green-600";}
  else if (["zip","gz","tar"].includes(ext))                                                   { icon = FiArchive;  col = "text-amber-500";}
  else if (["js","ts","py","html","css","json","xml"].includes(ext))                           { icon = FiCode;     col = "text-violet-500";}
  else if (["txt","md"].includes(ext))                                                         { icon = FiFileText; col = "text-kfaint"; }
  return <Ic icon={icon} className={`${className} ${col}`} />;
}

// Full MIME strings (e.g. the 72-char docx one) blow out the list-table
// width; show a short label and keep the full type in the tooltip.
const MIME_SHORT_LABELS: Record<string, string> = {
  "application/pdf": "pdf",
  "application/json": "json",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "application/msword": "doc",
  "application/vnd.ms-excel": "xls",
  "text/plain": "txt",
  "text/html": "html",
  "text/csv": "csv",
  "text/markdown": "md",
};

function shortFileType(fileType: string, filename: string): string {
  if (fileType && MIME_SHORT_LABELS[fileType]) return MIME_SHORT_LABELS[fileType];
  const ext = filename.includes(".") ? (filename.split(".").pop() ?? "").toLowerCase() : "";
  if (ext && ext.length <= 5) return ext;
  const sub = fileType?.split("/")[1] ?? "";
  if (!sub) return "—";
  return sub.length > 8 ? `${sub.slice(0, 8)}…` : sub;
}

/** Display name: the AI-suggested canonical name when present, else the
 * original filename. Icons, previews, and type badges keep using the
 * ORIGINAL filename (the suggested name has no extension). */
function displayName(u: UploadRecord): string {
  return u.newFilename?.trim() || u.filename;
}

function isPdf(u: UploadRecord): boolean {
  return (
    u.fileType === "application/pdf" ||
    u.filename.toLowerCase().endsWith(".pdf")
  );
}

// Word/RTF uploads are converted to PDF by the parse worker and the converted
// copy is stored (#101) — previewable via /api/uploads/:fileId/pdf.
const CONVERTIBLE_EXT = /\.(docx|dotx|docm|dotm|doc|dot|rtf)$/i;
function hasPdfPreview(u: UploadRecord): boolean {
  return isPdf(u) || CONVERTIBLE_EXT.test(u.filename);
}

/** Prefer the persisted uploads.format_hint; otherwise infer from filename / MIME. */
function resolveFormatHint(u: UploadRecord): string | null {
  if (u.formatHint) return u.formatHint;
  if (u.filename.endsWith("-earnings-transcript.json")) return "earnings_transcript";
  if (u.fileType === "application/json") return "edgar_companyfacts";
  return null;
}

// ── File tree types + helpers ─────────────────────────────────────────────────

interface FileRowActions {
  onPreview:      (u: UploadRecord) => void;
  onParsed:       (u: UploadRecord) => void;
  onTypesense:    (u: UploadRecord) => void;
  onSummary:      (u: UploadRecord) => void;
  onCosts:        (u: UploadRecord) => void;
  onRecord:       (u: UploadRecord) => void;
  onRetrigger:    (u: UploadRecord) => void;
  onReprocess:    (fileId: string) => void;
  retriggeringIds: Set<string>;
  reprocessingIds: Set<string>;
}

interface DocTypeGroup {
  docType: string;
  authors: { name: string; uploads: UploadRecord[] }[];
}

interface CorpusGroup {
  corpusId: string;
  label: string;
  groups: DocTypeGroup[];
}

// Corpus → document type → author → files. Type/author are joined into the
// uploads listing server-side (#112) — no separate metadata fetch.
function buildFilesTree(
  uploads: UploadRecord[],
  corpusLabels: Map<string, string>,
): CorpusGroup[] {
  const corpusMap = new Map<string, Map<string, Map<string, UploadRecord[]>>>();
  for (const u of uploads) {
    const corpus = u.owner || "(No corpus)";
    const dt = u.documentType?.trim() || "(No type)";
    const au = u.author?.trim() || "(Unknown)";
    if (!corpusMap.has(corpus)) corpusMap.set(corpus, new Map());
    const typeMap = corpusMap.get(corpus)!;
    if (!typeMap.has(dt)) typeMap.set(dt, new Map());
    const authorMap = typeMap.get(dt)!;
    if (!authorMap.has(au)) authorMap.set(au, []);
    authorMap.get(au)!.push(u);
  }
  return Array.from(corpusMap.entries())
    .map(([corpusId, typeMap]) => ({
      corpusId,
      label: corpusLabels.get(corpusId) ?? corpusId,
      groups: Array.from(typeMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([docType, authorMap]) => ({
          docType,
          authors: Array.from(authorMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([name, uploads]) => ({ name, uploads })),
        })),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ── Tree components (file-explorer style) ─────────────────────────────────────

// Shared row height & indent constants for a VSCode-style explorer feel
const ROW_H    = "h-7"; // 28 px rows
const INDENT_1 = "pl-4"; // corpus level (root)
const INDENT_2 = "pl-8"; // doc-type level
const INDENT_3 = "pl-12"; // author level
const INDENT_4 = "pl-16"; // file level

function ActionBtn({ title, onClick, disabled, className, children }: {
  title: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      className={`p-1 rounded hover:bg-krule disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0 ${className ?? "text-kfaint"}`}
    >
      {children}
    </button>
  );
}

function FileExplorerItem({ upload, actions, depth = 0 }: {
  upload: UploadRecord;
  actions: FileRowActions;
  depth?: number;
}) {
  const [selected, setSelected] = useState(false);
  const indent = depth === 0 ? INDENT_1 : depth === 1 ? INDENT_2 : depth === 2 ? INDENT_3 : INDENT_4;
  const statusDot: Record<string, string> = {
    complete:           "bg-green-400",
    error:              "bg-red-400",
    processing:         "bg-ksky",
    parsed:             "bg-cyan-400",
    pending:            "bg-yellow-400",
    "pending reprocess": "bg-yellow-400",
  };
  const dot = statusDot[upload.status] ?? "bg-krule";

  return (
    <div
      role="row"
      onClick={() => setSelected(p => !p)}
      className={`relative flex items-center gap-1.5 h-10 ${indent} pr-2 cursor-pointer select-none group
        ${selected ? "bg-kamber/15 text-ink" : "text-inklight hover:bg-paper"}`}
    >
      {/* spacer instead of chevron — files have no children */}
      <span className="w-4 shrink-0" />
      <FileTypeIcon filename={upload.filename} fileType={upload.fileType} className="w-4 h-4 shrink-0" />
      <span className="flex-1 min-w-0 flex flex-col justify-center overflow-hidden">
        <span className="text-[13px] truncate" title={upload.filename}>{displayName(upload)}</span>
        <span
          className="text-[10px] text-kfaint font-mono truncate cursor-pointer hover:text-ksky"
          title={`Click to copy: ${upload.fileId}`}
          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(upload.fileId); }}
        >
          {upload.fileId}
        </span>
      </span>
      {/* status dot — always in flow at pr-2, aligned with folder counts */}
      <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} title={upload.status} />
      {/* action buttons — absolute overlay so they don't shift the status dot */}
      <div className="absolute right-2 inset-y-0 flex items-center gap-0.5 bg-inherit">
        {hasPdfPreview(upload) && (
          <ActionBtn title="Preview PDF" onClick={() => actions.onPreview(upload)} className="text-kslate hover:text-ink">
            <Ic icon={FiEye} className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
        {upload.parsedS3Key && (
          <ActionBtn title="Parsed output" onClick={() => actions.onParsed(upload)} className="text-kslate hover:text-green-600">
            <Ic icon={FiFileText} className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
        {upload.parsedS3Key && (
          <ActionBtn title="Typesense index" onClick={() => actions.onTypesense(upload)} className="text-kslate hover:text-orange-500">
            <Ic icon={FiDatabase} className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
        {upload.status === "complete" && (
          <ActionBtn title="AI summary" onClick={() => actions.onSummary(upload)} className="text-kslate hover:text-purple-600">
            <Ic icon={FiClipboard} className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
        {upload.status === "complete" && (
          <ActionBtn title="LLM costs" onClick={() => actions.onCosts(upload)} className="text-kslate hover:text-emerald-600">
            <Ic icon={FiDollarSign} className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
        {upload.status === "complete" && (
          <ActionBtn title="Document record (metadata)" onClick={() => actions.onRecord(upload)} className="text-kslate hover:text-ksky">
            <Ic icon={FiInfo} className="w-3.5 h-3.5" />
          </ActionBtn>
        )}
        <ActionBtn
          title="Retrigger indexing (skip re-ingest, re-run index step only)"
          onClick={() => actions.onRetrigger(upload)}
          disabled={actions.retriggeringIds.has(upload.fileId)}
          className="text-kslate hover:text-ink"
        >
          <Ic icon={FiZap} className={`w-3.5 h-3.5 ${actions.retriggeringIds.has(upload.fileId) ? "animate-spin" : ""}`} />
        </ActionBtn>
        <ActionBtn
          title="Re-ingest document (full pipeline from scratch)"
          onClick={() => actions.onReprocess(upload.fileId)}
          disabled={actions.reprocessingIds.has(upload.fileId)}
          className="text-kslate hover:text-red-500"
        >
          <Ic icon={FiRefreshCw} className={`w-3.5 h-3.5 ${actions.reprocessingIds.has(upload.fileId) ? "animate-spin" : ""}`} />
        </ActionBtn>
      </div>
    </div>
  );
}

function FileAuthorNode({ name, uploads, actions }: {
  name: string;
  uploads: UploadRecord[];
  actions: FileRowActions;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        role="row"
        onClick={() => setOpen(p => !p)}
        className={`flex items-center gap-1.5 ${ROW_H} ${INDENT_3} pr-2 cursor-pointer select-none text-kslate hover:bg-paper group`}
      >
        <Ic icon={FiChevronRight} className={`w-3.5 h-3.5 shrink-0 text-kfaint transition-transform ${open ? "rotate-90" : ""}`} />
        <Ic icon={FiFolder} className={`w-4 h-4 shrink-0 ${open ? "text-amber-500" : "text-amber-400"}`} />
        <Ic icon={FiUser} className="w-3 h-3 shrink-0 text-kfaint" />
        <span className="flex-1 min-w-0 text-[13px] truncate" title={name}>{name}</span>
        <span className="text-[11px] text-kfaint shrink-0">{uploads.length}</span>
      </div>
      {open && uploads.map(u => (
        <FileExplorerItem key={u.fileId} upload={u} actions={actions} depth={3} />
      ))}
    </>
  );
}

function FileDocTypeNode({ group, actions }: {
  group: DocTypeGroup;
  actions: FileRowActions;
}) {
  const [open, setOpen] = useState(false);
  const total = group.authors.reduce((s, a) => s + a.uploads.length, 0);
  return (
    <>
      <div
        role="row"
        onClick={() => setOpen(p => !p)}
        className={`flex items-center gap-1.5 ${ROW_H} ${INDENT_2} pr-2 cursor-pointer select-none text-ink hover:bg-paper font-medium`}
      >
        <Ic icon={FiChevronRight} className={`w-3.5 h-3.5 shrink-0 text-kslate transition-transform ${open ? "rotate-90" : ""}`} />
        <Ic icon={FiFolder} className={`w-4 h-4 shrink-0 ${open ? "text-amber-600" : "text-amber-500"}`} />
        <span className="flex-1 min-w-0 text-[13px] truncate" title={group.docType}>{group.docType}</span>
        <span className="text-[11px] text-kfaint shrink-0">{total}</span>
      </div>
      {open && group.authors.map(a => (
        <FileAuthorNode key={a.name} name={a.name} uploads={a.uploads} actions={actions} />
      ))}
    </>
  );
}

function FileCorpusNode({ group, actions }: {
  group: CorpusGroup;
  actions: FileRowActions;
}) {
  const [open, setOpen] = useState(true);
  const total = group.groups.reduce((s, g) => s + g.authors.reduce((t, a) => t + a.uploads.length, 0), 0);
  return (
    <>
      <div
        role="row"
        onClick={() => setOpen(p => !p)}
        className={`flex items-center gap-1.5 ${ROW_H} ${INDENT_1} pr-2 cursor-pointer select-none text-ink hover:bg-paper font-semibold bg-paper/50`}
      >
        <Ic icon={FiChevronRight} className={`w-3.5 h-3.5 shrink-0 text-kslate transition-transform ${open ? "rotate-90" : ""}`} />
        <Ic icon={FiDatabase} className="w-4 h-4 shrink-0 text-ksky" />
        <span className="flex-1 min-w-0 text-[13px] truncate" title={group.corpusId}>{group.label}</span>
        <span className="text-[11px] text-kfaint shrink-0">{total}</span>
      </div>
      {open && group.groups.map(g => (
        <FileDocTypeNode key={g.docType} group={g} actions={actions} />
      ))}
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; classes: string }> = {
    pending:            { label: "Pending",          classes: "bg-yellow-100 text-yellow-800" },
    "pending reprocess":{ label: "Pending Reprocess",classes: "bg-yellow-100 text-yellow-800" },
    processing:         { label: "Processing",       classes: "bg-blue-100 text-blue-800" },
    parsed:             { label: "Parsed",           classes: "bg-cyan-100 text-cyan-800" },
    complete:           { label: "Complete",         classes: "bg-green-100 text-green-800" },
    error:              { label: "Error",            classes: "bg-red-100 text-red-800" },
  };
  const { label, classes } = cfg[status] ?? { label: status, classes: "bg-paper text-inklight" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${classes}`}>
      {label}
    </span>
  );
}

// ── Parsed Output Viewer Modal ────────────────────────────────────────────────

interface TextBlock {
  text?: string;
  source?: string;
  confidence?: number | null;
  label?: string;
}

interface CompactedFigure {
  figure_type?: string;
  description?: string;
  data?: unknown;
  bbox?: number[];
  image_b64?: string | null;
}

interface CompactedTable {
  caption?: string;
  description?: string;
  headers?: (string | null)[];
  rows?: (string | null)[][];
  row_spans?: unknown;
  data?: unknown;
  bbox?: number[];
  source?: string;
  page_num?: number;
}

interface CompactedPage {
  page_num?: number;
  page_type?: string;
  status?: string;
  text_blocks?: TextBlock[];
  tables?: CompactedTable[];
  figures?: CompactedFigure[];
  extraction_log?: string[];
  error_message?: string | null;
}

interface CompactedFixture {
  schema_version?: string;
  extraction_timestamp?: string;
  pages?: CompactedPage[];
}

// ── Block panels ──────────────────────────────────────────────────────────────

const BLOCK_STYLES: Record<string, { border: string; header: string; label: string }> = {
  text:   { border: "border-krule",   header: "bg-paper/50 text-kslate",       label: "Text" },
  table:  { border: "border-blue-200",   header: "bg-blue-50 text-blue-700",        label: "Table" },
  figure: { border: "border-purple-200", header: "bg-purple-50 text-purple-700",    label: "Figure" },
};

const LABEL_BADGE: Record<string, string> = {
  section_header: "bg-indigo-100 text-indigo-700",
  title:          "bg-indigo-100 text-indigo-700",
  list_item:      "bg-teal-100 text-teal-700",
  text:           "bg-paper text-kslate",
  caption:        "bg-yellow-100 text-yellow-700",
  table:          "bg-blue-100 text-blue-700",
  figure:         "bg-purple-100 text-purple-700",
};

function BlockPanel({ type, heading, children, onShowRaw }: { type: keyof typeof BLOCK_STYLES; heading: string; children: React.ReactNode; onShowRaw?: () => void }) {
  const s = BLOCK_STYLES[type];
  return (
    <div className={`rounded-lg border ${s.border} overflow-hidden`}>
      <div className={`flex items-center justify-between px-3 py-1 ${s.header}`}>
        <span className="text-[11px] font-semibold uppercase tracking-wider">{heading}</span>
        {onShowRaw && (
          <button
            onClick={onShowRaw}
            title="View raw JSON"
            className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-current opacity-50 hover:opacity-100 transition-opacity leading-none"
          >
            {"{ }"}
          </button>
        )}
      </div>
      <div className="px-3 py-2.5">
        {children}
      </div>
    </div>
  );
}

function RawJsonModal({ heading, json, onClose }: { heading: string; json: unknown; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="flex flex-col w-full max-w-2xl max-h-[80vh] mx-4 bg-kterm text-ktermtext rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-ktermline shrink-0">
          <span className="text-xs font-mono text-kfaint truncate">{heading}</span>
          <button onClick={onClose} className="ml-4 shrink-0 text-kslate hover:text-ktermtext text-xl leading-none">×</button>
        </div>
        <pre className="flex-1 overflow-auto p-4 text-xs font-mono text-green-300 whitespace-pre-wrap">
          {JSON.stringify(json, null, 2)}
        </pre>
      </div>
    </div>
  );
}

interface ChartConfig {
  type: string;
  data: object;
  options?: object;
}

function ChartModal({ heading, config, onClose }: { heading: string; config: ChartConfig; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="flex flex-col w-full max-w-2xl mx-4 bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-krule shrink-0">
          <span className="text-sm font-medium text-inklight truncate">{heading}</span>
          <button onClick={onClose} className="ml-4 shrink-0 text-kfaint hover:text-inklight text-xl leading-none">×</button>
        </div>
        <div className="p-6">
          <Chart
            type={config.type as any}
            data={config.data as any}
            options={{ ...(config.options ?? {}), responsive: true, maintainAspectRatio: true } as any}
          />
        </div>
      </div>
    </div>
  );
}

function TableRenderer({ tbl }: { tbl: CompactedTable }) {
  const headers = tbl.headers ?? [];
  const rows = tbl.rows ?? [];

  // If we have structured headers/rows, render a proper HTML table
  if (rows.length > 0) {
    const hasHeaders = headers.length > 0 && headers.some((h) => h != null && h !== "");
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse">
          {hasHeaders && (
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th
                    key={i}
                    className="border border-krule bg-paper px-3 py-1.5 text-left font-semibold text-inklight whitespace-nowrap"
                  >
                    {h ? sanitizeText(h) : ""}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-paper/50"}>
                {(Array.isArray(row) ? row : [row]).map((cell, ci) => (
                  <td
                    key={ci}
                    className="border border-krule px-3 py-1.5 text-inklight align-top"
                  >
                    {cell != null ? sanitizeText(String(cell)) : ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Fallback: render raw data as JSON
  if (tbl.data != null) {
    return (
      <pre className="text-xs text-inklight overflow-x-auto whitespace-pre-wrap font-mono">
        {JSON.stringify(tbl.data, null, 2)}
      </pre>
    );
  }

  return <p className="text-xs text-kfaint italic">No structured data available.</p>;
}

// ── Passthrough JSON viewers (email + generic JSON) ───────────────────────────
// Emails and other passthrough formats (edgar companyfacts, earnings
// transcripts) skip docling, so their "parsed" output is the original JSON
// with no pages array. Render emails as a message view and everything else
// as a collapsible tree instead of "No pages found".

function looksLikeEmail(data: Record<string, unknown>): boolean {
  const hasEnvelope =
    typeof data.subject === "string" || typeof data.from === "string" || typeof data.sender === "string";
  const hasBody =
    typeof data.body === "string" || typeof data.text === "string" || typeof data.bodyText === "string";
  return hasEnvelope && hasBody;
}

// Mirrors the tolerant field extraction in indexing-worker emailMessage.ts so
// the viewer accepts the same shapes the pipeline does.
function emailStr(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function emailAddressList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(emailStr).filter(Boolean);
  const s = emailStr(value);
  return s ? s.split(/[,;]/).map((v) => v.trim()).filter(Boolean) : [];
}

function EmailHeaderRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-14 shrink-0 text-kfaint">{label}</span>
      <span className={`text-inklight break-all ${mono ? "font-mono text-xs pt-0.5" : ""}`}>{value}</span>
    </div>
  );
}

function EmailMessageView({ raw, onShowRaw }: { raw: Record<string, unknown>; onShowRaw: () => void }) {
  const from = emailStr(raw.from) || emailStr(raw.sender) || "(unknown sender)";
  const to = emailAddressList(raw.to);
  const cc = emailAddressList(raw.cc);
  const subject = emailStr(raw.subject) || "(no subject)";
  const messageId = emailStr(raw.messageId) || emailStr(raw.message_id);
  const body = emailStr(raw.body) || emailStr(raw.text) || emailStr(raw.bodyText);
  const dateStr = emailStr(raw.date) || emailStr(raw.sentAt) || emailStr(raw.timestamp);
  const parsedDate = dateStr ? new Date(dateStr) : null;
  const dateLabel = parsedDate && !Number.isNaN(parsedDate.getTime()) ? formatDate(parsedDate.toISOString()) : dateStr;
  const attachments = Array.isArray(raw.attachments) ? raw.attachments : [];
  const attachmentNames = attachments
    .map((a) => (a && typeof a === "object"
      ? emailStr((a as Record<string, unknown>).fileName) || emailStr((a as Record<string, unknown>).name)
      : emailStr(a)))
    .filter(Boolean);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-krule overflow-hidden">
        <div className="flex items-center justify-between px-4 py-1.5 bg-paper/50">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-kslate">Email message</span>
          <button
            onClick={onShowRaw}
            title="View raw JSON"
            className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-current text-kslate opacity-50 hover:opacity-100 transition-opacity leading-none"
          >
            {"{ }"}
          </button>
        </div>
        <div className="px-4 py-3 space-y-1.5">
          <h2 className="text-base font-semibold text-ink">{subject}</h2>
          <EmailHeaderRow label="From" value={from} />
          <EmailHeaderRow label="To" value={to.join(", ")} />
          <EmailHeaderRow label="Cc" value={cc.join(", ")} />
          <EmailHeaderRow label="Date" value={dateLabel} />
          <EmailHeaderRow label="ID" value={messageId} mono />
          {attachmentNames.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1.5">
              {attachmentNames.map((name, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-paper text-kslate">
                  <Ic icon={FiPaperclip} className="w-3 h-3" />
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="rounded-lg border border-krule px-4 py-3">
        <p className="text-sm leading-relaxed whitespace-pre-wrap text-inklight">
          {body || <span className="italic text-kfaint">(empty body)</span>}
        </p>
      </div>
    </div>
  );
}

function JsonDocumentView({ raw, onShowRaw }: { raw: Record<string, unknown>; onShowRaw: () => void }) {
  return (
    <div className="rounded-lg border border-krule overflow-hidden">
      <div className="flex items-center justify-between px-4 py-1.5 bg-paper/50">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-kslate">JSON document</span>
        <button
          onClick={onShowRaw}
          title="View raw JSON"
          className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-current text-kslate opacity-50 hover:opacity-100 transition-opacity leading-none"
        >
          {"{ }"}
        </button>
      </div>
      <div className="px-3 py-2.5 font-mono text-xs overflow-x-auto">
        <JsonTreeNode name={null} value={raw} depth={0} />
      </div>
    </div>
  );
}

// ── Document record modal (#110) ─────────────────────────────────────────────
// The full processed_documents record — metadata (skill-extracted fields,
// facets, links), dates, S3 keys, status — as a collapsible tree.

const INDEXING_API_BASE = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:3001";

function DocumentRecordModal({ upload, onClose }: { upload: UploadRecord; onClose: () => void }) {
  const [record, setRecord] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  useEffect(() => {
    let cancelled = false;
    setRecord(null);
    setError(null);
    authFetch(`${INDEXING_API_BASE}/query/document/${upload.fileId}`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        return body as Record<string, unknown>;
      })
      .then((json) => { if (!cancelled) setRecord(json); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [upload.fileId, authFetch]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60" onClick={onClose}>
      <div
        className="flex flex-col w-full h-full max-w-3xl mx-auto my-6 bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-krule shrink-0">
          <div className="min-w-0">
            <span className="text-sm font-medium text-ink truncate block">Document record</span>
            <span className="text-xs text-kfaint font-mono truncate block">{upload.fileId}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {record && (
              <button
                onClick={() => navigator.clipboard.writeText(JSON.stringify(record, null, 2))}
                className="text-[11px] font-mono px-2 py-0.5 rounded border border-krule text-kslate hover:bg-paper transition-colors"
              >
                copy
              </button>
            )}
            <button onClick={onClose} className="ml-2 text-kfaint hover:text-inklight text-xl leading-none">×</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 font-mono text-xs">
          {!record && !error && <div className="flex items-center justify-center h-32 text-kslate text-sm">Loading…</div>}
          {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-sans">{error}</div>}
          {record && <JsonTreeNode name={null} value={record} depth={0} openDepth={2} />}
        </div>
      </div>
    </div>
  );
}

interface ParsedViewerModalProps {
  upload: UploadRecord;
  onClose: () => void;
}

function ParsedViewerModal({ upload, onClose }: ParsedViewerModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<(CompactedFixture & Record<string, unknown>) | null>(null);
  const [rawJson, setRawJson] = useState<{ heading: string; value: unknown } | null>(null);
  const [chartData, setChartData] = useState<{ heading: string; config: ChartConfig } | null>(null);
  const authFetch = useAuthFetch();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch(`${CORPUS_API}/api/uploads/${upload.fileId}/parsed`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<CompactedFixture & Record<string, unknown>>;
      })
      .then((json) => { if (!cancelled) { setData(json); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [upload.fileId, authFetch]);

  // Passthrough formats (email, edgar companyfacts, transcripts) have no
  // pages array — their parsed output IS the original JSON.
  const shape: "docling" | "email" | "json" | null =
    data == null ? null
    : Array.isArray(data.pages) ? "docling"
    : looksLikeEmail(data) ? "email"
    : "json";

  const stats = shape === "docling" && data?.pages ? {
    pages: data.pages.length,
    textBlocks: data.pages.reduce((n, p) => n + (p.text_blocks?.length ?? 0), 0),
    tables: data.pages.reduce((n, p) => n + (p.tables?.length ?? 0), 0),
    figures: data.pages.reduce((n, p) => n + (p.figures?.length ?? 0), 0),
  } : null;

  return (
    <>
      {rawJson && (
        <RawJsonModal heading={rawJson.heading} json={rawJson.value} onClose={() => setRawJson(null)} />
      )}
      {chartData && (
        <ChartModal heading={chartData.heading} config={chartData.config} onClose={() => setChartData(null)} />
      )}
      <div className="fixed inset-0 z-50 flex flex-col bg-black/60" onClick={onClose}>
      <div
        className="flex flex-col w-full h-full max-w-4xl mx-auto my-6 bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-krule shrink-0">
          <div className="min-w-0">
            <span className="text-sm font-medium text-ink truncate block">{upload.filename}</span>
            {stats && (
              <span className="text-xs text-kfaint">
                {stats.pages} pages · {stats.textBlocks} text blocks · {stats.tables} tables · {stats.figures} figures
              </span>
            )}
            {shape === "email" && <span className="text-xs text-kfaint">Email message</span>}
            {shape === "json" && data && (
              <span className="text-xs text-kfaint">JSON document · {Object.keys(data).length} top-level keys</span>
            )}
          </div>
          <button onClick={onClose} className="ml-4 shrink-0 text-kfaint hover:text-inklight text-xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-8">
          {loading && <div className="flex items-center justify-center h-32 text-kslate text-sm">Loading…</div>}
          {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          {data && shape === "email" && (
            <EmailMessageView
              raw={data}
              onShowRaw={() => setRawJson({ heading: upload.filename, value: data })}
            />
          )}

          {data && shape === "json" && (
            <JsonDocumentView
              raw={data}
              onShowRaw={() => setRawJson({ heading: upload.filename, value: data })}
            />
          )}

          {shape === "docling" && data && (data.pages ?? []).map((page, pi) => {
            const pageLabel = `Page ${(page.page_num ?? pi) + 1}`;
            const textBlocks = (page.text_blocks ?? []) as TextBlock[];
            const tables = page.tables ?? [];
            const figures = page.figures ?? [];
            const hasContent = textBlocks.length > 0 || tables.length > 0 || figures.length > 0;

            return (
              <section key={pi}>
                {/* Page heading */}
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-sm font-bold text-inklight">{pageLabel}</h3>
                  {page.page_type && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-paper text-kslate">{page.page_type}</span>
                  )}
                  {page.status && page.status !== "success" && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">{page.status}</span>
                  )}
                  {page.extraction_log && page.extraction_log.length > 0 && (
                    <span className="text-xs text-kfaint font-mono">{page.extraction_log[0]}</span>
                  )}
                </div>

                {!hasContent && (
                  <p className="text-xs text-kfaint italic ml-1">No content extracted for this page.</p>
                )}

                <div className="space-y-2">
                  {/* Text blocks */}
                  {textBlocks.map((rawBlock, bi) => {
                    const block: TextBlock = rawBlock == null
                      ? {}
                      : typeof rawBlock === "string"
                        ? { text: rawBlock, label: "text" }
                        : (rawBlock as TextBlock);
                    const text = sanitizeText(block.text?.trim() ?? "");
                    if (!text) return null;
                    const labelKey = block.label ?? "text";
                    const badgeClass = LABEL_BADGE[labelKey] ?? LABEL_BADGE.text;
                    const isHeader = labelKey === "section_header" || labelKey === "title";
                    return (
                      <BlockPanel key={`t${bi}`} type="text" heading={`Text block ${bi + 1}`} onShowRaw={() => setRawJson({ heading: `${pageLabel} · Text block ${bi + 1}`, value: rawBlock })}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badgeClass}`}>
                            {labelKey.replace(/_/g, " ")}
                          </span>
                          {block.confidence != null && (
                            <span className="text-[11px] text-kfaint font-mono shrink-0">
                              conf {block.confidence.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm leading-relaxed whitespace-pre-wrap ${isHeader ? "font-semibold text-ink" : "text-inklight"}`}>
                          {text}
                        </p>
                      </BlockPanel>
                    );
                  })}

                  {/* Tables */}
                  {tables.map((tbl, ti) => (
                    <BlockPanel key={`tbl${ti}`} type="table" heading={tbl.caption ? `Table — ${tbl.caption}` : `Table ${ti + 1}`} onShowRaw={() => setRawJson({ heading: `${pageLabel} · Table ${ti + 1}`, value: tbl })}>
                      <TableRenderer tbl={tbl} />
                      {tbl.description && (
                        <p className="mt-2 text-sm text-kslate leading-relaxed italic border-t border-krule/60 pt-2">{tbl.description}</p>
                      )}
                    </BlockPanel>
                  ))}

                  {/* Figures */}
                  {figures.map((fig, fi) => (
                    <BlockPanel key={`f${fi}`} type="figure" heading={`Figure ${fi + 1}`} onShowRaw={() => setRawJson({ heading: `${pageLabel} · Figure ${fi + 1}`, value: fig })}>
                      {/* Type badge */}
                      {fig.figure_type && (
                        <span className={`inline-block mb-2 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                          fig.figure_type === "chart"       ? "bg-purple-100 text-purple-700"
                          : fig.figure_type === "infographic" ? "bg-pink-100 text-pink-700"
                          : "bg-paper text-kslate"
                        }`}>
                          {fig.figure_type}
                        </span>
                      )}

                      {/* Image */}
                      {fig.image_b64 && (
                        <img
                          src={`data:image/png;base64,${fig.image_b64}`}
                          alt={fig.description || `Figure ${fi + 1}`}
                          className="max-w-full rounded mb-2"
                        />
                      )}

                      {/* Description */}
                      {fig.description ? (
                        <p className="text-sm text-inklight leading-relaxed mb-2">{fig.description}</p>
                      ) : !fig.image_b64 && (
                        <p className="text-xs text-kfaint italic mb-1">
                          No image data available.
                          {fig.bbox && (
                            <span className="ml-2 font-mono text-krule">
                              bbox [{fig.bbox.map((v) => v.toFixed(3)).join(", ")}]
                            </span>
                          )}
                        </p>
                      )}

                      {/* Chart.js render button */}
                      {fig.data != null && (
                        <button
                          className="mt-2 text-[11px] font-semibold px-3 py-1 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                          onClick={() => setChartData({ heading: `${pageLabel} · Figure ${fi + 1}`, config: fig.data as ChartConfig })}
                        >
                          Render chart
                        </button>
                      )}
                    </BlockPanel>
                  ))}
                </div>
              </section>
            );
          })}

          {shape === "docling" && data && (data.pages ?? []).length === 0 && (
            <p className="text-sm text-kslate">No pages found in parsed output.</p>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

// ── LLM Costs Modal ───────────────────────────────────────────────────────────

const INDEXING_API = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:3001";

interface LlmCostCall {
  id: number;
  action_id: string;
  llm_model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: string;
  operation: string | null;
  recorded_at: string;
}

interface LlmCostRun {
  actionId: string;
  firstRecordedAt: string;
  calls: LlmCostCall[];
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalCostUsd: number;
}

interface LlmCostsResponse {
  fileId: string;
  runCount: number;
  runs: LlmCostRun[];
}

function LlmCostsModal({ upload, onClose }: { upload: UploadRecord; onClose: () => void }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LlmCostsResponse | null>(null);
  const authFetch = useAuthFetch();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch(`${INDEXING_API}/query/costs/${upload.fileId}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<LlmCostsResponse>;
      })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [upload.fileId, authFetch]);

  const grandTotal = data?.runs.reduce((s, r) => s + r.totalCostUsd, 0) ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60" onClick={onClose}>
      <div
        className="flex flex-col w-full h-full max-w-4xl mx-auto my-6 bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-krule shrink-0">
          <div className="min-w-0">
            <span className="text-sm font-medium text-ink truncate block">{upload.filename}</span>
            <span className="text-xs text-kfaint">LLM indexing costs</span>
          </div>
          {data && (
            <span className="mx-4 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1 shrink-0">
              Grand total: ${grandTotal.toFixed(6)}
            </span>
          )}
          <button onClick={onClose} className="ml-2 shrink-0 text-kfaint hover:text-inklight text-xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && <div className="flex items-center justify-center h-32 text-kslate text-sm">Loading…</div>}
          {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          {data && data.runs.map((run, ri) => (
            <div key={run.actionId} className="rounded-xl border border-krule overflow-hidden">
              {/* Run header */}
              <div className="flex items-center gap-4 px-4 py-2 bg-paper/50 border-b border-krule">
                <span className="text-xs font-bold text-kslate uppercase tracking-wide">
                  Run {ri + 1}
                </span>
                <span className="text-xs font-mono text-kfaint truncate flex-1" title={run.actionId}>
                  {run.actionId}
                </span>
                <span className="text-xs text-kslate whitespace-nowrap shrink-0">
                  {new Date(run.firstRecordedAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </span>
                <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 shrink-0">
                  ${run.totalCostUsd.toFixed(6)}
                </span>
              </div>

              {/* Calls table */}
              <table className="min-w-full text-xs divide-y divide-krule/60">
                <thead className="bg-paper/50/60">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-kfaint uppercase tracking-wider">Operation</th>
                    <th className="px-3 py-2 text-left font-medium text-kfaint uppercase tracking-wider">Model</th>
                    <th className="px-3 py-2 text-right font-medium text-kfaint uppercase tracking-wider">Prompt</th>
                    <th className="px-3 py-2 text-right font-medium text-kfaint uppercase tracking-wider">Completion</th>
                    <th className="px-3 py-2 text-right font-medium text-kfaint uppercase tracking-wider">Total</th>
                    <th className="px-3 py-2 text-right font-medium text-kfaint uppercase tracking-wider">Cost (USD)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-krule/40">
                  {run.calls.map((call) => (
                    <tr key={call.id} className="hover:bg-paper/50">
                      <td className="px-3 py-1.5 text-inklight font-medium">{call.operation ?? "—"}</td>
                      <td className="px-3 py-1.5 text-kslate font-mono truncate max-w-[180px]" title={call.llm_model}>{call.llm_model}</td>
                      <td className="px-3 py-1.5 text-right text-kslate">{call.prompt_tokens.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right text-kslate">{call.completion_tokens.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right text-kslate">{call.total_tokens.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-emerald-700">${parseFloat(call.cost_usd).toFixed(6)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-paper/50 border-t border-krule">
                  <tr>
                    <td colSpan={2} className="px-3 py-2 text-xs font-semibold text-kslate uppercase">Run total</td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-inklight">{run.totalPromptTokens.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-inklight">{run.totalCompletionTokens.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-inklight">{run.totalTokens.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-emerald-700">${run.totalCostUsd.toFixed(6)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Typesense Document Modal ──────────────────────────────────────────────────

// ── AI Summary Modal ──────────────────────────────────────────────────────────

interface PageSummary {
  page: number;
  summary: string;
}

interface SectionSummary {
  unit: number;
  pageNums?: number[];
  summary: string;
}

interface AnalysisResult {
  document_type?: string;
  document_date?: string;
  suggested_name?: string;
  summary_scope?: string;
  summary?: string;
  keywords?: string[];
  entities?: string[];
  page_summaries?: PageSummary[];
  section_summaries?: SectionSummary[];
  /** Unit type the analysis was built from: "section" | "page". The
   *  page_summaries field name is historic — its entries are these units. */
  analysis_unit?: string;
}

interface SummaryModalProps {
  upload: UploadRecord;
  onClose: () => void;
}

function SummaryModal({ upload, onClose }: SummaryModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [showAllKeywords, setShowAllKeywords] = useState(false);
  const [expandedUnits, setExpandedUnits] = useState<Set<number>>(new Set());
  const authFetch = useAuthFetch();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch(`${INDEXING_API}/query/analysis/${upload.fileId}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<AnalysisResult>;
      })
      .then((d) => { if (!cancelled) { setAnalysis(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [upload.fileId, authFetch]);

  const units = analysis?.section_summaries ?? analysis?.page_summaries?.map((p) => ({
    unit: p.page,
    summary: p.summary,
  })) ?? [];

  const unitLabel =
    analysis?.section_summaries || analysis?.analysis_unit === "section" ? "Section" : "Page";

  const toggleUnit = (i: number) =>
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const KEYWORD_LIMIT = 20;
  const keywords = analysis?.keywords ?? [];
  const visibleKeywords = showAllKeywords ? keywords : keywords.slice(0, KEYWORD_LIMIT);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60" onClick={onClose}>
      <div
        className="flex flex-col w-full h-full max-w-3xl mx-auto my-6 bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-krule shrink-0">
          <div className="min-w-0">
            <span className="text-sm font-medium text-ink truncate block">{upload.filename}</span>
            <span className="text-xs text-kfaint">AI analysis</span>
          </div>
          <button onClick={onClose} className="ml-4 shrink-0 text-kfaint hover:text-inklight text-xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && <div className="flex items-center justify-center h-32 text-kslate text-sm">Loading…</div>}
          {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          {analysis && (
            <>
              {/* Meta */}
              <div className="bg-paper/50 rounded-lg px-4 py-2 divide-y divide-krule/60">
                <MetaRow label="Document type" value={analysis.document_type} />
                <MetaRow label="Document date" value={analysis.document_date} />
                <MetaRow label="Suggested name" value={<span className="font-mono text-xs">{analysis.suggested_name}</span>} />
                <MetaRow label="Summary scope" value={analysis.summary_scope} />
              </div>

              {/* Summary */}
              {analysis.summary && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-kfaint mb-2">Summary</h4>
                  <p className="text-sm text-ink leading-relaxed bg-ksky/10 border border-ksky/30 rounded-lg px-4 py-3">
                    {analysis.summary}
                  </p>
                </div>
              )}

              {/* Keywords */}
              {keywords.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-kfaint mb-2">Keywords</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {visibleKeywords.map((k) => (
                      <span key={k} className="px-2 py-0.5 rounded text-xs font-medium bg-paper text-inklight">{k}</span>
                    ))}
                    {keywords.length > KEYWORD_LIMIT && (
                      <button
                        onClick={() => setShowAllKeywords(!showAllKeywords)}
                        className="px-2 py-0.5 rounded text-xs font-medium bg-krule text-kslate hover:bg-krule"
                      >
                        {showAllKeywords ? "Show less" : `+${keywords.length - KEYWORD_LIMIT} more`}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Entities */}
              {(analysis.entities ?? []).length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-kfaint mb-2">Entities</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.entities!.map((e) => (
                      <span key={e} className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">{e}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Per-unit summaries */}
              {units.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-kfaint mb-2">
                    {unitLabel} summaries <span className="font-normal normal-case">({units.length})</span>
                  </h4>
                  <div className="space-y-1.5">
                    {units.map((u, i) => {
                      const expanded = expandedUnits.has(i);
                      const PREVIEW = 200;
                      const shown = !expanded && u.summary.length > PREVIEW
                        ? u.summary.slice(0, PREVIEW) + "…"
                        : u.summary;
                      return (
                        <div key={i} className="rounded-lg border border-krule overflow-hidden">
                          <div className="flex items-center gap-3 px-3 py-1.5 bg-paper/50 text-xs">
                            <span className="font-semibold text-kslate">{unitLabel} {u.unit + 1}</span>
                            {"pageNums" in u && (u as SectionSummary).pageNums?.length ? (
                              <span className="text-kfaint">p. {(u as SectionSummary).pageNums!.join(", ")}</span>
                            ) : null}
                          </div>
                          <div className="px-3 py-2">
                            <p className="text-sm text-inklight leading-relaxed">{shown}</p>
                            {u.summary.length > PREVIEW && (
                              <button
                                onClick={() => toggleUnit(i)}
                                className="mt-1 text-xs text-ink hover:text-inklight"
                              >
                                {expanded ? "Show less" : "Show all"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


interface TypesenseSection {
  sectionId: string;
  unitNum: number;
  pageNums: number[];
  contentLength: number;
  content: string;
}

interface TypesenseDoc {
  fileId: string;
  title?: string;
  tenantId?: string;
  createdAt?: number;
  s3Key?: string;
  parsedS3Key?: string;
  categories?: string[];
  economicSector?: string;
  industrySector?: string;
  geographicArea?: string;
  assetClass?: string;
  timePeriod?: string;
  tickers?: string[];
  principalCompanies?: string[];
  author?: string;
  pageCount?: number;
  sectionCount?: number;
  sections?: TypesenseSection[];
}

function MetaRow({ label, value }: { label: string; value?: React.ReactNode }) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="flex gap-3 py-1.5 border-b border-krule/60 last:border-0">
      <span className="w-36 shrink-0 text-xs font-semibold uppercase tracking-wide text-kfaint">{label}</span>
      <span className="text-sm text-ink break-words min-w-0">{value}</span>
    </div>
  );
}

function TagList({ items, color }: { items: string[]; color: string }) {
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((t) => (
        <span key={t} className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>{t}</span>
      ))}
    </div>
  );
}

function SectionPanel({ section }: { section: TypesenseSection }) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 400;
  const text = section.content ?? "";
  const shown = !expanded && text.length > PREVIEW ? text.slice(0, PREVIEW) + "…" : text;

  return (
    <div className="rounded-lg border border-krule overflow-hidden">
      <div className="flex items-center gap-3 px-3 py-1.5 bg-paper/50 text-xs">
        <span className="font-semibold text-kslate">Section {section.unitNum}</span>
        {section.pageNums?.length > 0 && (
          <span className="text-kfaint">p. {section.pageNums.join(", ")}</span>
        )}
        <span className="ml-auto text-kfaint font-mono">{section.contentLength.toLocaleString()} chars</span>
      </div>
      <div className="px-3 py-2.5">
        <p className="text-sm text-inklight leading-relaxed whitespace-pre-wrap">{shown}</p>
        {text.length > PREVIEW && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1.5 text-xs text-ink hover:text-inklight"
          >
            {expanded ? "Show less" : `Show all (${text.length.toLocaleString()} chars)`}
          </button>
        )}
      </div>
    </div>
  );
}

interface TypesenseDocumentModalProps {
  upload: UploadRecord;
  onClose: () => void;
}

function TypesenseDocumentModal({ upload, onClose }: TypesenseDocumentModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [doc, setDoc] = useState<TypesenseDoc | null>(null);
  const authFetch = useAuthFetch();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch(`${INDEXING_API}/query/typesense/${upload.fileId}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<TypesenseDoc>;
      })
      .then((d) => { if (!cancelled) { setDoc(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [upload.fileId, authFetch]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60" onClick={onClose}>
      <div
        className="flex flex-col w-full h-full max-w-3xl mx-auto my-6 bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-krule shrink-0 bg-white">
          <div className="min-w-0">
            <span className="text-sm font-medium text-ink truncate block">{upload.filename}</span>
            <span className="text-xs text-kfaint">Typesense index record</span>
          </div>
          <button onClick={onClose} className="ml-4 shrink-0 text-kfaint hover:text-inklight text-xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <div className="flex items-center justify-center h-32 text-kslate text-sm">Loading…</div>}
          {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          {doc && (
            <div className="space-y-5">
              {/* Core fields */}
              <div className="bg-paper/50 rounded-lg px-4 py-2 divide-y divide-krule/60">
                <MetaRow label="File ID"    value={<span className="font-mono text-xs">{doc.fileId}</span>} />
                <MetaRow label="Title"      value={<span className="font-semibold">{doc.title}</span>} />
                <MetaRow label="Tenant"     value={doc.tenantId} />
                <MetaRow label="Created"    value={doc.createdAt ? new Date(doc.createdAt * 1000).toLocaleString() : undefined} />
                <MetaRow label="Sections"   value={doc.sectionCount != null ? `${doc.sectionCount} sections` : undefined} />
                <MetaRow label="Pages"      value={doc.pageCount != null ? `${doc.pageCount}` : undefined} />
                <MetaRow label="S3 key"     value={<span className="font-mono text-xs break-all">{doc.s3Key}</span>} />
                <MetaRow label="Parsed key" value={<span className="font-mono text-xs break-all">{doc.parsedS3Key}</span>} />
              </div>

              {/* Classification */}
              {(doc.categories?.length || doc.economicSector || doc.industrySector ||
                doc.geographicArea || doc.assetClass || doc.timePeriod || doc.author ||
                doc.tickers?.length || doc.principalCompanies?.length) && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-kfaint mb-2">Classification</h4>
                  <div className="bg-paper/50 rounded-lg px-4 py-2 divide-y divide-krule/60">
                    {doc.categories?.length ? <MetaRow label="Categories"  value={<TagList items={doc.categories} color="bg-blue-100 text-blue-700" />} /> : null}
                    <MetaRow label="Econ. sector" value={doc.economicSector} />
                    <MetaRow label="Industry"     value={doc.industrySector} />
                    <MetaRow label="Geography"    value={doc.geographicArea} />
                    <MetaRow label="Asset class"  value={doc.assetClass} />
                    <MetaRow label="Time period"  value={doc.timePeriod} />
                    <MetaRow label="Author"       value={doc.author} />
                    {doc.tickers?.length ? <MetaRow label="Tickers"   value={<TagList items={doc.tickers} color="bg-teal-100 text-teal-700" />} /> : null}
                    {doc.principalCompanies?.length ? <MetaRow label="Companies" value={<TagList items={doc.principalCompanies} color="bg-krule text-inklight" />} /> : null}
                  </div>
                </div>
              )}

              {/* Sections */}
              {doc.sections && doc.sections.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-kfaint mb-2">
                    Sections <span className="font-normal normal-case">({doc.sections.length})</span>
                  </h4>
                  <div className="space-y-2">
                    {doc.sections.map((s) => (
                      <SectionPanel key={s.sectionId} section={s} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PDF Preview Modal ─────────────────────────────────────────────────────────

interface PdfPreviewModalProps {
  upload: UploadRecord;
  onClose: () => void;
}

function PdfPreviewModal({ upload, onClose }: PdfPreviewModalProps) {
  // <embed> can't send an Authorization header, so fetch the PDF as a blob
  // with the Clerk token and render it via an object URL.
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setBlobUrl(null);
    setError(null);
    authFetch(`${CORPUS_API}/api/uploads/${upload.fileId}/pdf`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
        }
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        // Force the PDF type: a generic content-type on the response would
        // make <embed> download instead of render (#101).
        objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        setBlobUrl(objectUrl);
      })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [upload.fileId, authFetch]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60" onClick={onClose}>
      <div
        className="flex flex-col w-full h-full max-w-5xl mx-auto my-6 bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-krule shrink-0">
          <span className="text-sm font-medium text-ink truncate">{upload.filename}</span>
          <button
            onClick={onClose}
            className="ml-4 text-kfaint hover:text-inklight text-xl leading-none shrink-0"
          >
            ×
          </button>
        </div>
        {/* PDF embed */}
        {error ? (
          <div className="flex-1 flex items-center justify-center text-sm text-red-600">{error}</div>
        ) : blobUrl ? (
          <embed
            src={blobUrl}
            type="application/pdf"
            className="flex-1 w-full"
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-kslate">Loading PDF…</div>
        )}
      </div>
    </div>
  );
}

// ── Upload Modal ──────────────────────────────────────────────────────────────

interface UploadModalProps {
  onClose: () => void;
  onComplete: () => void;
  /** Corpora the user can add documents to (owned or write-permitted). */
  writableCorpora: CorpusListing[];
  /** The user's default (private) corpus id. */
  defaultCorpusId: string | null;
}

function UploadModal({ onClose, onComplete, writableCorpora, defaultCorpusId }: UploadModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [dragging, setDragging] = useState(false);
  const [targetCorpus, setTargetCorpus] = useState(defaultCorpusId ?? "");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const authFetch = useAuthFetch();

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    setSelectedFiles((prev) => {
      const existing = new Set(prev.map((f) => f.name));
      const incoming = Array.from(files).filter((f) => !existing.has(f.name));
      return [...prev, ...incoming];
    });
    setResults([]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (name: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.name !== name));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    const uploadResults: UploadResult[] = [];

    for (const file of selectedFiles) {
      // Files land in the selected owned corpus (default: the caller's
      // private corpus, which the server also uses when corpusId is omitted).
      const formData = new FormData();
      formData.append("file", file);
      if (targetCorpus) formData.append("corpusId", targetCorpus);

      try {
        const res = await authFetch(`${CORPUS_API}/api/upload`, {
          method: "POST",
          body: formData,
        });

        if (res.status === 202) {
          uploadResults.push({ filename: file.name, status: "success", message: "Queued for processing" });
        } else if (res.status === 409) {
          const data = await res.json();
          uploadResults.push({ filename: file.name, status: "duplicate", message: `Duplicate — already uploaded as ${data.existing?.fileId ?? "unknown"}` });
        } else {
          const data = await res.json().catch(() => ({}));
          uploadResults.push({ filename: file.name, status: "error", message: data.error ?? `HTTP ${res.status}` });
        }
      } catch (err) {
        uploadResults.push({ filename: file.name, status: "error", message: (err as Error).message ?? "Network error" });
      }
    }

    setResults(uploadResults);
    setUploading(false);

    const anySuccess = uploadResults.some((r) => r.status === "success");
    if (anySuccess) onComplete();

    // Auto-close if all succeeded
    if (uploadResults.every((r) => r.status === "success")) {
      setTimeout(onClose, 1200);
    }
  };

  const done = results.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-krule">
          <h2 className="text-base font-semibold text-ink">Upload Files</h2>
          <button onClick={onClose} className="text-kfaint hover:text-kslate text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Target corpus — only corpora the user owns accept documents */}
          {!done && (
            <div className="flex items-center gap-3">
              <label className="text-sm text-inklight shrink-0" htmlFor="upload-corpus">Corpus</label>
              <select
                id="upload-corpus"
                value={targetCorpus}
                onChange={(e) => setTargetCorpus(e.target.value)}
                disabled={uploading}
                className="flex-1 text-sm border border-krule rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-kamber/70"
              >
                {writableCorpora.map((c) => (
                  <option key={c.corpusId} value={c.corpusId}>
                    {corpusLabel(c)}{c.isDefault ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Drop zone */}
          {!done && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragging ? "border-ksky bg-ksky/10" : "border-krule hover:border-ksky hover:bg-paper/50"
              }`}
            >
              <div className="text-3xl mb-2">📂</div>
              <p className="text-sm text-kslate">
                Drag files here or <span className="text-ink font-medium">browse</span>
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
            </div>
          )}

          {/* Selected files */}
          {!done && selectedFiles.length > 0 && (
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {selectedFiles.map((f) => (
                <li key={f.name} className="flex items-center justify-between text-sm bg-paper/50 rounded px-3 py-1.5">
                  <span className="truncate text-inklight">{f.name}</span>
                  <span className="text-kfaint text-xs ml-2 shrink-0">{formatBytes(f.size)}</span>
                  <button onClick={() => removeFile(f.name)} className="ml-2 text-kfaint hover:text-red-500 shrink-0">×</button>
                </li>
              ))}
            </ul>
          )}

          {/* Results */}
          {done && (
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {results.map((r) => (
                <li key={r.filename} className={`flex items-start gap-2 text-sm rounded px-3 py-2 ${
                  r.status === "success" ? "bg-green-50 text-green-800" :
                  r.status === "duplicate" ? "bg-yellow-50 text-yellow-800" :
                  "bg-red-50 text-red-800"
                }`}>
                  <span className="shrink-0">{r.status === "success" ? "✓" : r.status === "duplicate" ? "⚠" : "✗"}</span>
                  <span>
                    <span className="font-medium">{r.filename}</span>
                    <span className="ml-1 font-normal opacity-80">— {r.message}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-krule bg-paper/50">
          <button onClick={onClose} className="text-sm text-kslate hover:text-ink px-4 py-2 rounded-md hover:bg-krule transition-colors">
            {done ? "Close" : "Cancel"}
          </button>
          {!done && (
            <button
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
              className="text-sm font-medium bg-ink text-white px-4 py-2 rounded-md hover:bg-inklight disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? "Uploading…" : `Upload ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ""}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const FilesPage: React.FC = () => {
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [previewUpload, setPreviewUpload] = useState<UploadRecord | null>(null);
  const [parsedViewUpload, setParsedViewUpload] = useState<UploadRecord | null>(null);
  const [recordViewUpload, setRecordViewUpload] = useState<UploadRecord | null>(null);
  const [typesenseViewUpload, setTypesenseViewUpload] = useState<UploadRecord | null>(null);
  const [summaryViewUpload, setSummaryViewUpload] = useState<UploadRecord | null>(null);
  const [costsViewUpload, setCostsViewUpload] = useState<UploadRecord | null>(null);
  const [reprocessingIds,  setReprocessingIds]  = useState<Set<string>>(new Set());
  const [retriggeringIds,  setRetriggeringIds]  = useState<Set<string>>(new Set());
  const [viewMode,         setViewMode]         = useState<"list" | "tree">("list");
  // Server-side paging (#112): the list shows one page; the tree fetches the
  // full (slim) listing since it groups across all files.
  const [page,  setPage]  = useState(1);
  const [total, setTotal] = useState(0);
  const authFetch = useAuthFetch();
  const { accessible: accessibleCorpora, writable: writableCorpora, defaultCorpusId } = useCorpora();

  const handleRetrigger = async (u: UploadRecord) => {
    setRetriggeringIds((prev) => new Set(prev).add(u.fileId));
    try {
      // console.log("retriggering", JSON.stringify(u, null, 2));

      const res = await authFetch(`${INDEXING_API}/query/test-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic: "corpus_parsed",
          partition: 0,
          message: {
            offset: "0",
            value: {
              fileId: u.fileId,
              owner: u.owner,
              filename: u.filename,
              formatHint: resolveFormatHint(u),
              fileType: u.fileType,
              fileSize: u.fileSize,
              // The indexing pipeline reads the parsed docling JSON, not the
              // original upload — real corpus_parsed messages carry the parsed
              // key. Fall back to the raw key only when parsing never ran
              // (e.g. edgar companyfacts JSON passed through unparsed).
              s3Key: u.parsedS3Key ?? u.s3Key,
              bucket: process.env.REACT_APP_S3_BUCKET ?? "cassia-corpus-dev",
              endpoint: process.env.REACT_APP_S3_ENDPOINT ?? "https://atl1.digitaloceanspaces.com",
              region: process.env.REACT_APP_S3_REGION ?? "us-east-1",
              hash: "",
              hashType: "sha256",
              parsedAt: new Date().toISOString(),
              pageCount: 0,
            },
          },
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert((body as { error?: string }).error ?? `Retrigger failed: HTTP ${res.status}`);
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setRetriggeringIds((prev) => { const s = new Set(prev); s.delete(u.fileId); return s; });
    }
  };

  const handleReprocess = async (fileId: string) => {
    setReprocessingIds((prev) => new Set(prev).add(fileId));
    try {
      const res = await authFetch(`${CORPUS_API}/api/uploads/${fileId}/reprocess`, { method: "POST" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert((body as { error?: string }).error ?? `Reprocess failed: HTTP ${res.status}`);
      } else {
        await fetchUploads();
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setReprocessingIds((prev) => { const s = new Set(prev); s.delete(fileId); return s; });
    }
  };
  // Optional corpus narrowing; empty = all corpora visible to the user.
  const [corpusFilter, setCorpusFilter] = useState("");
  const PAGE_SIZE = 50;

  const fetchUploads = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (corpusFilter.trim()) params.set("corpusId", corpusFilter.trim());
      if (viewMode === "tree") {
        // The tree groups across every file — fetch the full slim listing.
        params.set("page", "1");
        params.set("perPage", "10000");
      } else {
        params.set("page", String(page));
        params.set("perPage", String(PAGE_SIZE));
      }
      const res = await authFetch(`${CORPUS_API}/api/uploads?${params}`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setUploads(data.uploads ?? []);
      setTotal(typeof data.total === "number" ? data.total : (data.uploads ?? []).length);
    } catch (err) {
      if (!opts?.silent) setError((err as Error).message);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [corpusFilter, authFetch, viewMode, page]);

  // Refetch whenever the filter changes or the accessible corpus set changes
  // (e.g. the user subscribed/unsubscribed on the Corpora page).
  const accessibleKey = accessibleCorpora.map((c) => c.corpusId).sort().join("|");
  useEffect(() => {
    fetchUploads();
  }, [fetchUploads, accessibleKey]);

  // Drop the corpus filter if it points at a corpus that is no longer accessible.
  useEffect(() => {
    if (corpusFilter && !accessibleCorpora.some((c) => c.corpusId === corpusFilter)) {
      setCorpusFilter("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleKey]);

  // Reset paging when the scope or view changes.
  useEffect(() => { setPage(1); }, [corpusFilter, viewMode]);

  // Auto-refresh (#112): silent poll while the tab is visible, so arriving
  // files appear without touching Refresh. Preserves paging and tree
  // expansion (the fetch replaces data; component state is untouched).
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchUploads({ silent: true });
    }, 10_000);
    return () => clearInterval(id);
  }, [fetchUploads]);

  const corpusLabels = useMemo(
    () => new Map(accessibleCorpora.map((c) => [c.corpusId, corpusLabel(c)])),
    [accessibleCorpora],
  );

  const treeGroups = useMemo(
    () => buildFilesTree(uploads, corpusLabels),
    [uploads, corpusLabels],
  );

  const treeActions: FileRowActions = {
    onPreview:      setPreviewUpload,
    onParsed:       setParsedViewUpload,
    onTypesense:    setTypesenseViewUpload,
    onSummary:      setSummaryViewUpload,
    onCosts:        setCostsViewUpload,
    onRecord:       setRecordViewUpload,
    onRetrigger:    handleRetrigger,
    onReprocess:    handleReprocess,
    retriggeringIds,
    reprocessingIds,
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 h-[78px] border-b border-krule bg-white shrink-0">
        <div>
          <h2 className="text-xl font-light tracking-tight text-ink">Files</h2>
          <p className="text-xs font-mono text-kslate mt-0.5">Corpus upload pipeline</p>
        </div>
        <div className="flex-1" />
        {/* Corpus filter — all corpora accessible to the user */}
        <select
          value={corpusFilter}
          onChange={(e) => setCorpusFilter(e.target.value)}
          title="Filter by corpus"
          className="text-sm border border-krule rounded-md px-3 py-1.5 w-44 bg-white focus:outline-none focus:ring-2 focus:ring-kamber/70"
        >
          <option value="">All corpora</option>
          {accessibleCorpora.map((c) => (
            <option key={c.corpusId} value={c.corpusId}>{corpusLabel(c)}</option>
          ))}
        </select>
        <button
          onClick={() => fetchUploads()}
          disabled={loading}
          title="Refresh file statuses"
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-kslate hover:text-ink hover:bg-paper border border-krule rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <span className={loading ? "animate-spin inline-block" : "inline-block"}>↻</span>
          Refresh
        </button>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 bg-ink hover:bg-inklight text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
        >
          <span>↑</span> Upload
        </button>
        {/* List / Tree toggle */}
        <div className="flex rounded-lg border border-krule overflow-hidden text-xs font-medium">
          <button type="button" onClick={() => setViewMode("list")}
            className={`px-3 py-1.5 transition-colors ${viewMode === "list" ? "bg-ink text-white" : "bg-white text-kslate hover:bg-paper/50"}`}
          >List</button>
          <button type="button" onClick={() => setViewMode("tree")}
            className={`px-3 py-1.5 border-l border-krule transition-colors ${viewMode === "tree" ? "bg-ink text-white" : "bg-white text-kslate hover:bg-paper/50"}`}
          >Tree</button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading && (
          <div className="flex items-center justify-center h-48 text-kslate text-sm">
            Loading…
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {!loading && !error && uploads.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-kfaint">
            <div className="text-5xl mb-4">📭</div>
            <p className="text-sm">No files uploaded yet.</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="mt-4 text-sm text-ink hover:underline"
            >
              Upload your first file
            </button>
          </div>
        )}

        {/* Tree view */}
        {!loading && !error && uploads.length > 0 && viewMode === "tree" && (
          <div className="bg-white border border-krule rounded-lg overflow-hidden">
            {treeGroups.map(g => (
              <FileCorpusNode key={g.corpusId} group={g} actions={treeActions} />
            ))}
          </div>
        )}

        {/* List view (table) */}
        {!loading && !error && uploads.length > 0 && viewMode === "list" && (
          <>
          <div className="bg-white rounded-lg border border-krule overflow-hidden">
            <table className="min-w-full divide-y divide-krule text-sm">
              <thead className="bg-paper/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-kslate uppercase tracking-wider w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-kslate uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-kslate uppercase tracking-wider">Owner</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-kslate uppercase tracking-wider">Size</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-kslate uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-kslate uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-kslate uppercase tracking-wider">Uploaded</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-krule/60">
                {uploads.map((u) => (
                  <tr key={u.fileId} className="hover:bg-paper/50 transition-colors">
                    <td className="px-4 py-3"><FileTypeIcon filename={u.filename} fileType={u.fileType} className="w-5 h-5" /></td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-ink truncate block max-w-xs" title={u.filename}>
                        {displayName(u)}
                      </span>
                      <span
                        className="text-xs text-kfaint font-mono truncate block max-w-xs cursor-pointer hover:text-ksky"
                        title={`Click to copy: ${u.fileId}`}
                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(u.fileId); }}
                      >
                        {u.fileId}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-kslate">
                      <span className="truncate block max-w-[9rem]" title={u.owner}>{u.owner}</span>
                    </td>
                    <td className="px-4 py-3 text-kslate whitespace-nowrap">{formatBytes(u.fileSize)}</td>
                    <td className="px-4 py-3">
                      <span
                        className="text-xs font-mono px-2 py-0.5 rounded-full bg-paper text-kslate whitespace-nowrap"
                        title={u.fileType || undefined}
                      >
                        {shortFileType(u.fileType, u.filename)}
                      </span>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                    <td className="px-4 py-3 text-kslate whitespace-nowrap">{formatDate(u.uploadedAt)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {hasPdfPreview(u) && (
                          <button
                            onClick={() => setPreviewUpload(u)}
                            title="Preview PDF"
                            className="text-kfaint hover:text-ink transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        )}
                        {u.parsedS3Key && (
                          <button
                            onClick={() => setParsedViewUpload(u)}
                            title="View parsed output"
                            className="text-kfaint hover:text-green-600 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>
                        )}
                        {u.parsedS3Key && (
                          <button
                            onClick={() => setTypesenseViewUpload(u)}
                            title="View Typesense index record"
                            className="text-kfaint hover:text-orange-500 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                            </svg>
                          </button>
                        )}
                        {u.status === "complete" && (
                          <button
                            onClick={() => setSummaryViewUpload(u)}
                            title="View AI summary"
                            className="text-kfaint hover:text-purple-600 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                          </button>
                        )}
                        {u.status === "complete" && (
                          <button
                            onClick={() => setCostsViewUpload(u)}
                            title="View LLM indexing costs"
                            className="text-kfaint hover:text-emerald-600 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                        {u.status === "complete" && (
                          <button
                            onClick={() => setRecordViewUpload(u)}
                            title="Document record (metadata)"
                            className="text-kfaint hover:text-ksky transition-colors"
                          >
                            <Ic icon={FiInfo} className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRetrigger(u)}
                          disabled={retriggeringIds.has(u.fileId)}
                          title="Retrigger indexing (skip re-ingest, re-run index step only)"
                          className="text-kfaint hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {retriggeringIds.has(u.fileId) ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleReprocess(u.fileId)}
                          disabled={reprocessingIds.has(u.fileId)}
                          title="Re-ingest document (full pipeline from scratch)"
                          className="text-kfaint hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {reprocessingIds.has(u.fileId) ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-3 text-sm text-kslate">
              <span>
                {((page - 1) * PAGE_SIZE + 1).toLocaleString()}–{Math.min(page * PAGE_SIZE, total).toLocaleString()} of {total.toLocaleString()} files
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 border border-krule rounded-md hover:bg-paper disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ← Prev
                </button>
                <button
                  disabled={page * PAGE_SIZE >= total}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 border border-krule rounded-md hover:bg-paper disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onComplete={fetchUploads}
          writableCorpora={writableCorpora}
          defaultCorpusId={defaultCorpusId}
        />
      )}

      {previewUpload && (
        <PdfPreviewModal
          upload={previewUpload}
          onClose={() => setPreviewUpload(null)}
        />
      )}

      {recordViewUpload && (
        <DocumentRecordModal upload={recordViewUpload} onClose={() => setRecordViewUpload(null)} />
      )}
      {parsedViewUpload && (
        <ParsedViewerModal
          upload={parsedViewUpload}
          onClose={() => setParsedViewUpload(null)}
        />
      )}

      {typesenseViewUpload && (
        <TypesenseDocumentModal
          upload={typesenseViewUpload}
          onClose={() => setTypesenseViewUpload(null)}
        />
      )}

      {summaryViewUpload && (
        <SummaryModal
          upload={summaryViewUpload}
          onClose={() => setSummaryViewUpload(null)}
        />
      )}

      {costsViewUpload && (
        <LlmCostsModal
          upload={costsViewUpload}
          onClose={() => setCostsViewUpload(null)}
        />
      )}

    </div>
  );
};

export default FilesPage;
